import { EnvironmentProviders, Injector, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { JwtService } from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../account/account.service';

/**
 * Reads the session stored in the device (tokens and account) and applies it to the app.
 *
 * The account is only restored when there are credentials: with them, its snapshot (capabilities,
 * company, language) lets the guards and the menus work without connectivity. Without them the
 * snapshot is dropped, so a stale one cannot keep the user authenticated.
 *
 * The `username` is not required: a session received by link (`validateToken`) applies the tokens
 * without it, and requiring it would throw away a perfectly valid stored session.
 *
 * **The account service is resolved after the credentials are applied, on purpose**: it drops the
 * stored snapshot when it sees a session state of `false` with no credentials, which is exactly the
 * state during the bootstrap. Resolving it first deleted the snapshot on every start, so a cold start
 * without connectivity could not recognise the session (with connectivity the verification rebuilt it,
 * which is why it only failed offline).
 */
export async function bootstrapStoredSession(injector: Injector): Promise<void> {
  const storage = injector.get(StorageRepository);
  const jwtService = injector.get(JwtService);

  const [accessToken, refreshToken, username] = await Promise.all([
    storage.read('accessToken'),
    storage.read('refreshToken'),
    storage.read('username'),
  ]);

  if (!accessToken.value || !refreshToken.value) {
    // There is no session to trust: the stored snapshot must not keep the user authenticated.
    injector.get(AccountService).clearIdentity();

    return;
  }

  jwtService.setAccessToken(accessToken.value);
  jwtService.setRefreshToken(refreshToken.value);

  if (username.value) {
    jwtService.setUsername(username.value);
  }

  await injector.get(AccountService).restoreAccountFromStorage();
}

/**
 * @description
 * Applies the stored session during the app bootstrap, before the first component and the initial
 * navigation, so a cold start already knows if there is a session and what the user can do.
 */
export function provideSessionBootstrap(): EnvironmentProviders {
  return makeEnvironmentProviders([provideAppInitializer(() => bootstrapStoredSession(inject(Injector)))]);
}
